import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import {
	Text,
	useDisclosure,
	Spinner,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiMove } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import { cn } from "utils/helpers";
import { showToast } from "utils/helpers";
import type {
	ScheduleColumn,
	ScheduleColumnType,
	ScheduleRow,
	ScheduleOfRatesData,
} from "./schedule-of-rates.types";
import {
	getScheduleOfRates,
	saveScheduleOfRates,
	deleteScheduleOfRates,
	initScheduleFromTemplate,
} from "../../api";
import { Modal } from "newui/components/ui/Modal";

export interface ScheduleOfRatesHandle {
	validate: () => boolean;
	save: () => Promise<boolean>;
}

export interface ScheduleOfRatesProps {
	disabled: boolean;
	hasValidationError?: boolean;
	inviteId: number;
	isClientView: boolean;
}

const COLUMN_TYPE_LABELS: Record<ScheduleColumnType, string> = {
	text: "Text",
	unit_of_measurement: "Unit",
	number: "Number",
	currency: "Currency",
};

const COLUMN_TYPE_OPTIONS: { label: string; value: ScheduleColumnType }[] = [
	{ value: "text", label: "Text Column" },
	{ value: "unit_of_measurement", label: "Unit of Measurement Column" },
	{ value: "number", label: "Number Column" },
	{ value: "currency", label: "Currency Column" },
];

const cellBorder = "border border-[#EAECF0]";

const generateSortId = (columns: ScheduleColumn[]): number => {
	if (columns.length === 0) {
		return 1;
	}
	return Math.max(...columns.map((c) => c.sortOrder)) + 1;
};

const generateLocalId = (): number => {
	return -Math.floor(Math.random() * 1_000_000_000);
};

const formatCurrencyValue = (value: string): string => {
	const num = parseFloat(value);
	if (isNaN(num)) {
		return value;
	}
	return num.toFixed(2);
};

const ScheduleOfRates = forwardRef<ScheduleOfRatesHandle, ScheduleOfRatesProps>(({
	disabled,
	hasValidationError = false,
	inviteId,
	isClientView,
}, ref) => {
	const [schedule, setSchedule] = useState<ScheduleOfRatesData | null>(null);
	const [columns, setColumns] = useState<ScheduleColumn[]>([]);
	const [rows, setRows] = useState<ScheduleRow[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isInitializing, setIsInitializing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showEditor, setShowEditor] = useState(false);
	const [dragOverColId, setDragOverColId] = useState<number | null>(null);
	const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isRowColumnDeleteOpen, onOpen: onRowColumnDeleteOpen, onClose: onRowColumnDeleteClose } = useDisclosure();
	const [deleteTarget, setDeleteTarget] = useState<{ type: "row"; id: number } | { type: "column"; id: number } | null>(null);
	const { values: unitsAndMeasures } = useUnitsAndMeasures();

	const applyScheduleData = useCallback((data: ScheduleOfRatesData) => {
		setSchedule(data);
		setColumns(data.columns);
		setRows(data.rows);
	}, []);

	const validateSchedule = useCallback((): boolean => {
		// No content means nothing to validate
		if (columns.length === 0 || rows.length === 0) {
			return true;
		}
		const errors = new Set<string>();
		rows.forEach((row) => {
			columns.forEach((col) => {
				const value = row.values[col.id] ?? "";
				if (!value.trim()) {
					errors.add(`${row.id}-${col.id}`);
				}
			});
		});
		if (errors.size > 0) {
			setValidationErrors(errors);
			return false;
		}
		setValidationErrors(new Set());
		return true;
	}, [columns, rows]);

	useImperativeHandle(ref, () => ({
		validate: validateSchedule,
		save: async (): Promise<boolean> => {
			if (!validateSchedule()) {
				return false;
			}
			// No content to save
			if (columns.length === 0 || rows.length === 0) {
				return true;
			}
			setIsSaving(true);
			try {
				const data = await saveScheduleOfRates(inviteId, {
					name: "Schedule of Rates",
					columns: columns.map((c) => ({
						name: c.label,
						type: c.columnType,
						sort_order: c.sortOrder,
					})),
					rows: rows.map((r) => ({
						sort_order: r.sortOrder,
						cells: r.values,
					})),
				});
				applyScheduleData(data);
				setShowEditor(false);
				return true;
			} catch {
				return false;
			} finally {
				setIsSaving(false);
			}
		},
	}), [validateSchedule, inviteId, columns, rows, applyScheduleData]);

	const fetchSchedule = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getScheduleOfRates(inviteId);
			if (data) {
				applyScheduleData(data);
			}
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, [inviteId, applyScheduleData]);

	useEffect(() => {
		fetchSchedule();
	}, [fetchSchedule]);

	const handleAddSchedule = useCallback(async () => {
		setIsInitializing(true);
		try {
			const data = await initScheduleFromTemplate(inviteId);
			if (data) {
				applyScheduleData(data);
			} else {
				// No company template — show empty editor
				setShowEditor(true);
			}
		} catch {
			// Init failed — show empty editor as fallback
			setShowEditor(true);
		} finally {
			setIsInitializing(false);
		}
	}, [inviteId, applyScheduleData]);

	const handleAddColumn = useCallback(
		(columnType: ScheduleColumnType) => {
			const nextSortOrder = generateSortId(columns);
			const newCol: ScheduleColumn = {
				id: nextSortOrder,
				label: `Column ${columns.length + 1}`,
				columnType,
				sortOrder: nextSortOrder,
			};
			setColumns([...columns, newCol]);
		},
		[columns]
	);

	const handleRemoveColumn = useCallback(
		(colId: number) => {
			setColumns(columns.filter((c) => c.id !== colId));
			setRows(
				rows.map((row) => {
					const newValues = { ...row.values };
					delete newValues[colId];
					return { ...row, values: newValues };
				})
			);
		},
		[columns, rows]
	);

	const handleColumnReorder = useCallback(
		(sourceColId: number, targetColId: number) => {
			if (sourceColId === targetColId) {
				return;
			}
			const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
			const sourceIdx = sorted.findIndex((c) => c.id === sourceColId);
			const targetIdx = sorted.findIndex((c) => c.id === targetColId);
			if (sourceIdx === -1 || targetIdx === -1) {
				return;
			}
			const [moved] = sorted.splice(sourceIdx, 1);
			sorted.splice(targetIdx, 0, moved);

			// Build old→new id mapping and reassign sortOrder = id
			const idMap: Record<number, number> = {};
			const reordered = sorted.map((col, idx) => {
				const newSortOrder = idx + 1;
				idMap[col.id] = newSortOrder;
				return { ...col, sortOrder: newSortOrder, id: newSortOrder };
			});

			// Re-key row cell values to match new column ids
			const reKeyedRows = rows.map((row) => {
				const newValues: Record<number, string> = {};
				for (const [oldKey, value] of Object.entries(row.values)) {
					const newKey = idMap[Number(oldKey)];
					if (newKey !== undefined) {
						newValues[newKey] = value;
					}
				}
				return { ...row, values: newValues };
			});

			setColumns(reordered);
			setRows(reKeyedRows);
		},
		[columns, rows]
	);

	const handleDragStart = useCallback(
		(e: React.DragEvent<HTMLTableCellElement>, colId: number) => {
			e.dataTransfer.setData("text/plain", String(colId));
			e.dataTransfer.effectAllowed = "move";
		},
		[]
	);

	const handleDragOver = useCallback(
		(e: React.DragEvent<HTMLTableCellElement>, colId: number) => {
			e.preventDefault();
			e.dataTransfer.dropEffect = "move";
			setDragOverColId(colId);
		},
		[]
	);

	const handleDragLeave = useCallback(() => {
		setDragOverColId(null);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLTableCellElement>, targetColId: number) => {
			e.preventDefault();
			setDragOverColId(null);
			const sourceColId = Number(e.dataTransfer.getData("text/plain"));
			handleColumnReorder(sourceColId, targetColId);
		},
		[handleColumnReorder]
	);

	const handleColumnLabelChange = useCallback(
		(colId: number, label: string) => {
			setColumns(
				columns.map((c) => (c.id === colId ? { ...c, label } : c))
			);
		},
		[columns]
	);

	const handleAddRow = useCallback(() => {
		const newRow: ScheduleRow = {
			id: generateLocalId(),
			sortOrder: rows.length + 1,
			values: {},
		};
		setRows([...rows, newRow]);
	}, [rows]);

	const handleRemoveRow = useCallback(
		(rowId: number) => {
			setRows(rows.filter((r) => r.id !== rowId));
		},
		[rows]
	);

	const confirmDeleteRowOrColumn = useCallback(
		(target: { type: "row"; id: number } | { type: "column"; id: number }) => {
			setDeleteTarget(target);
			onRowColumnDeleteOpen();
		},
		[onRowColumnDeleteOpen]
	);

	const handleRowColumnDelete = useCallback(() => {
		if (!deleteTarget) {
			return;
		}
		if (deleteTarget.type === "row") {
			handleRemoveRow(deleteTarget.id);
		} else {
			handleRemoveColumn(deleteTarget.id);
		}
		setDeleteTarget(null);
		onRowColumnDeleteClose();
	}, [deleteTarget, handleRemoveRow, handleRemoveColumn, onRowColumnDeleteClose]);

	const handleCellChange = useCallback(
		(rowId: number, colId: number, value: string) => {
			setRows(
				rows.map((row) => {
					if (row.id === rowId) {
						return {
							...row,
							values: { ...row.values, [colId]: value },
						};
					}
					return row;
				})
			);
			if (value.trim()) {
				setValidationErrors((prev) => {
					const next = new Set(prev);
					next.delete(`${rowId}-${colId}`);
					return next;
				});
			}
		},
		[rows]
	);

	const handleDelete = useCallback(async () => {
		if (!schedule) {
			return;
		}
		setIsDeleting(true);
		try {
			await deleteScheduleOfRates(inviteId, schedule.id);
			setSchedule(null);
			setColumns([]);
			setRows([]);
			setShowEditor(false);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
			onClose();
		}
	}, [schedule, inviteId, onClose]);

	const renderCellInput = useCallback(
		(row: ScheduleRow, col: ScheduleColumn) => {
			const value = row.values[col.id] ?? "";
			const hasError = validationErrors.has(`${row.id}-${col.id}`);
			const errorBorder = hasError ? "border-red-500" : "border-gray-300";

			if (disabled || isClientView) {
				let displayValue = value || "-";
				if (col.columnType === "currency" && value) {
					displayValue = `$${formatCurrencyValue(value)}`;
				}
				if (col.columnType === "unit_of_measurement" && value) {
					displayValue =
						unitsAndMeasures?.find((u) => u.value === Number(value))?.label ??
						value;
				}
				return (
					<span className="text-gray-900" data-testid={`cell-view-${row.id}-${col.id}`}>
						{displayValue}
					</span>
				);
			}

			if (col.columnType === "unit_of_measurement") {
				return (
					<select
						className={cn("w-full rounded border bg-white px-2 py-1 text-sm", errorBorder)}
						value={value}
						onChange={(e) => handleCellChange(row.id, col.id, e.target.value)}
						aria-label={`${col.label} for row ${rows.indexOf(row) + 1}`}
						data-testid={`cell-${row.id}-${col.id}`}
					>
						<option value="">Select unit</option>
						{unitsAndMeasures?.map((unit) => (
							<option key={unit.value} value={unit.value}>
								{unit.label}
							</option>
						))}
					</select>
				);
			}

			const inputType =
				col.columnType === "number" || col.columnType === "currency"
					? "number"
					: "text";

			return (
				<input
					type={inputType}
					className={cn("w-full rounded border bg-white px-2 py-1 text-sm", errorBorder)}
					value={value}
					onChange={(e) => handleCellChange(row.id, col.id, e.target.value)}
					aria-label={`${col.label} for row ${rows.indexOf(row) + 1}`}
					onBlur={() => {
						if (col.columnType === "currency" && value) {
							handleCellChange(row.id, col.id, formatCurrencyValue(value));
						}
					}}
					placeholder={col.columnType === "currency" ? "0.00" : ""}
					min={col.columnType === "number" || col.columnType === "currency" ? 0 : undefined}
					step={1}
					data-testid={`cell-${row.id}-${col.id}`}
				/>
			);
		},
		[disabled, isClientView, unitsAndMeasures, handleCellChange, rows, validationErrors]
	);

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-8"
				data-testid="schedule-loading"
			>
				<Spinner size="md" />
			</div>
		);
	}

	const hasSchedule = schedule !== null;
	const hasContent = columns.length > 0;
	const isEditable = !disabled && !isClientView;

	// No schedule exists and not in editor mode — show Add button (supplier) or nothing (client)
	if (!hasSchedule && !showEditor) {
		if (!isEditable) {
			return null;
		}
		return (
			<div
				className="mt-6 rounded-xl border border-dashed border-[#D0D5DD] bg-white p-6"
				data-testid="schedule-of-rates-add"
			>
				<div className="flex flex-col items-center gap-3">
					<Text className="font-roboto text-sm text-gray-500">
						No schedule of rates attached to this submission.
					</Text>
					<Button
						size="sm"
						btntype="primary"
						onClick={handleAddSchedule}
						disabled={isInitializing}
						data-testid="add-schedule-btn"
					>
						<FiPlus className="mr-1" />
						{isInitializing ? "Loading..." : "Add Schedule of Rates"}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"mt-6 mb-6 rounded-xl border bg-white shadow-sm",
				hasValidationError ? "border-2 border-red-500" : "border-[#EAECF0]"
			)}
			data-testid="schedule-of-rates"
		>
			<div className="flex items-center justify-between border-b border-[#EAECF0] px-4 py-3 bg-blue-100">
				<Text className="font-roboto text-lg font-semibold text-gray-900">
					Schedule of Rates
				</Text>
				<div className="flex items-center gap-2">
					{isEditable && (hasSchedule || showEditor) && (
						<Button
							size="xs"
							btntype="plain-error"
							className="rounded p-1 !hover:bg-red-50 !hover:text-red-600 !text-red-400 !bg-white !border-red-400"
							onClick={hasSchedule ? onOpen : () => {
								setShowEditor(false);
								setColumns([]);
								setRows([]);
							}}
							aria-label="Delete schedule"
							data-testid="delete-schedule-btn"

						>
							<FiTrash2 size={14} /> <span className="pl-2">Remove</span>
						</Button>
					)}
				</div>
			</div>

			{isEditable && (
				<div className="flex items-center gap-2 border-b border-[#EAECF0] px-4 py-2">
					<div className="flex gap-1">
						{COLUMN_TYPE_OPTIONS.map((opt) => (
							<Button
								key={opt.value}
								size="xs"
								btntype="secondary"
								onClick={() => handleAddColumn(opt.value)}
								data-testid={`add-col-${opt.value}`}
							>
								<FiPlus className="mr-1" />
								{opt.label}
							</Button>
						))}
					</div>
				</div>
			)}

			{hasContent && (
				<div className="overflow-x-auto">
					<table
						className="w-full table-auto"
						data-testid="schedule-table"
					>
						<caption className="sr-only">Schedule of Rates</caption>
						<thead>
							<tr className="bg-[#F9FAFB]">
								<th
									className={cn(
										cellBorder,
										"w-10 px-2 py-2 text-center font-roboto text-xs font-medium text-tertiary-600"
									)}
								>
									#
								</th>
								{columns.map((col) => (
									<th
										key={col.id}
										className={cn(
											cellBorder,
											"relative px-2 py-2 font-roboto text-xs font-medium text-tertiary-600",
											isEditable && "cursor-move",
											dragOverColId === col.id && "bg-blue-100"
										)}
										style={{ minWidth: "120px" }}
										draggable={isEditable ? true : undefined}
										onDragStart={isEditable ? (e) => handleDragStart(e, col.id) : undefined}
										onDragOver={isEditable ? (e) => handleDragOver(e, col.id) : undefined}
										onDragLeave={isEditable ? handleDragLeave : undefined}
										onDrop={isEditable ? (e) => handleDrop(e, col.id) : undefined}
									>
										{isEditable && (
											<FiMove
												size={10}
												className="absolute left-1 top-1 text-gray-400"
												title="Drag to reorder"
											/>
										)}
										<div className="flex items-center gap-1">
											{isEditable ? (
												<input
													type="text"
													className="w-full rounded border border-transparent bg-transparent px-1 text-xs font-medium hover:border-gray-300 focus:border-blue-400 focus:outline-none"
													value={col.label}
													onChange={(e) =>
														handleColumnLabelChange(col.id, e.target.value)
													}
													onMouseDown={(e) => e.stopPropagation()}
													aria-label={`Column label for ${col.label}`}
													data-testid={`col-label-${col.id}`}
												/>
											) : (
												<span>{col.label}</span>
											)}
											<span className="text-[10px] text-gray-400">
												({COLUMN_TYPE_LABELS[col.columnType]})
											</span>
											{isEditable && (
												<button
													type="button"
													className="ml-auto rounded p-0.5 text-red-400 hover:text-red-600"
													onClick={() => confirmDeleteRowOrColumn({ type: "column", id: col.id })}
													title="Remove column"
													aria-label={`Remove column ${col.label}`}
													data-testid={`remove-col-${col.id}`}
												>
													<FiTrash2 size={10} />
												</button>
											)}
										</div>
									</th>
								))}
								{isEditable && (
									<th className="w-10 bg-[#F9FAFB]" />
								)}
							</tr>
						</thead>
						<tbody>
							{rows.map((row, idx) => (
								<tr key={row.id} className="border-b border-[#EAECF0]">
									<td
										className={cn(
											cellBorder,
											"text-center font-roboto text-xs text-primary-500 bg-gray-50"
										)}
									>
										{idx + 1}
									</td>
									{columns.map((col) => (
										<td key={col.id} className={cn(cellBorder, "px-1 py-1")}>
											{renderCellInput(row, col)}
										</td>
									))}
									{isEditable && (
										<td className="px-1">
											<button
												type="button"
												className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
												onClick={() => confirmDeleteRowOrColumn({ type: "row", id: row.id })}
												title="Remove row"
												aria-label={`Remove row ${idx + 1}`}
												data-testid={`remove-row-${row.id}`}
											>
												<FiTrash2 size={12} />
											</button>
										</td>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{isEditable && (
				<div className="flex items-center gap-2 border-t border-[#EAECF0] px-4 py-2">
					{hasContent && (
						<Button
							size="xs"
							btntype="secondary"
							onClick={handleAddRow}
							data-testid="add-row-btn"
						>
							<FiPlus className="mr-1" />
							Add Row
						</Button>
					)}
				</div>
			)}
			<Modal isOpen={isOpen} onClose={onClose} title="Remove Schedule of Rates" closeOnEsc closeOnOverlayClick primaryButton={{ text: isDeleting ? "Deleting..." : "Delete", onClick: handleDelete }} secondaryButton={{ onClick: onClose, text: "Cancel" }} iconType="error">
				<Text>
					This will permanently delete your schedule of rates from this RFx Submission.
				</Text>
				<Text>
					This action cannot be undone.
				</Text>
			</Modal>
			<Modal isOpen={isRowColumnDeleteOpen} onClose={onRowColumnDeleteClose} title={`Remove ${deleteTarget?.type === "column" ? "Column" : "Row"}`} closeOnEsc closeOnOverlayClick primaryButton={{ text: "Delete", onClick: handleRowColumnDelete }} secondaryButton={{ onClick: onRowColumnDeleteClose, text: "Cancel" }} iconType="error">
				<Text>
					Are you sure you want to remove this {deleteTarget?.type === "column" ? "column" : "row"}?
				</Text>
				<Text>
					This action cannot be undone.
				</Text>
			</Modal>
		</div>
	);
});

ScheduleOfRates.displayName = "ScheduleOfRates";

export { ScheduleOfRates };
